import React from 'react';
import { Clock, Layers, Download, Shield, Image, Camera, Music, Video } from 'lucide-react';
import dark from '../assets/features/dark.png';
import light from '../assets/features/light.png';
import pc1 from '../assets/features/pc1.png';
import pc2 from '../assets/features/pc2.png';



const colorMap = {
  blue: { bg: "bg-[#4285F4]/10 dark:bg-[#4285F4]/20", icon: "bg-[#4285F4]", text: "text-[#4285F4]" },
  green: { bg: "bg-[#34A853]/10 dark:bg-[#34A853]/20", icon: "bg-[#34A853]", text: "text-[#34A853]" },
  red: { bg: "bg-[#EA4335]/10 dark:bg-[#EA4335]/20", icon: "bg-[#EA4335]", text: "text-[#EA4335]" },
  yellow: { bg: "bg-[#FBBC04]/10 dark:bg-[#FBBC04]/20", icon: "bg-[#FBBC04]", text: "text-[#FBBC04]" },
};

const FeatureCard = ({ IconComponent, color, title, description, delay = 0 }) => {
  const classes = colorMap[color] || colorMap.blue;
  const Icon = IconComponent;
  return (
    <div
      data-aos="fade-up"
      data-aos-delay={delay}
      className="glass-card p-8 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 group ripple-effect transform hover:scale-105 animate-fadeInUp"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className={`${classes.icon} p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6 transition-all duration-300 group-hover:scale-110 shadow-lg`}>
        <Icon className="text-white h-8 w-8" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <p className="text-gray-700 dark:text-gray-300">{description}</p>
    </div>
  );
};

const ToolCard = ({ IconComponent, bgColor, title, description }) => {
  const classes = colorMap[bgColor] || colorMap.blue;
  const Icon = IconComponent;
  return (
    <div className="glass-card p-6 rounded-2xl shadow-xl flex items-center hover:shadow-2xl transition-all duration-300 group ripple-effect transform hover:scale-105">
      <div className={`${classes.icon} p-4 rounded-xl mr-4 transition-all duration-300 group-hover:scale-110 shadow-lg`}>
        <Icon className="text-white h-6 w-6" />
      </div>
      <div>
        <h4 className="font-bold text-gray-900 dark:text-white text-lg">{title}</h4>
        <p className="text-sm text-gray-700 dark:text-gray-300">{description}</p>
      </div>
    </div>
  );
};


const FeaturesSection = () => {
  const features = [
    {
      IconComponent: Clock,
      color: "blue",
      title: "Time Saving",
      description: "Generate bulk content 10x faster than manual creation.",
      delay: 0
    },
    {
      IconComponent: Layers,
      color: "green",
      title: "Multi-Format",
      description: "Support for stories, images, videos, music, and more.",
      delay: 100
    },
    {
      IconComponent: Download,
      color: "red",
      title: "CSV Export",
      description: "Organized data export for easy content management.",
      delay: 200
    },
    {
      IconComponent: Shield,
      color: "yellow",
      title: "Secure",
      description: "Fully compliant with Chrome Web Store policies.",
      delay: 300
    }
  ];

  const tools = [
    {
      IconComponent: Image,
      bgColor: "green",
      title: "Whisk",
      description: "Creative image generation"
    },
    {
      IconComponent: Camera,
      bgColor: "blue",
      title: "ImageFX",
      description: "Text-to-image creation"
    },
    {
      IconComponent: Music,
      bgColor: "yellow",
      title: "MusicFX",
      description: "Music and sound generation"
    },
    {
      IconComponent: Video,
      bgColor: "red",
      title: "Flow",
      description: "AI video creation"
    }
  ];

  return (
    <section id="features" className="py-20 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
      {/* Google-themed ambient backgrounds */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse bg-[#4285F4]"></div>
        <div className="absolute top-1/3 right-1/4 w-80 h-80 rounded-full blur-2xl animate-pulse bg-[#EA4335]" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 rounded-full blur-3xl animate-pulse bg-[#FBBC04]" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-1/3 right-1/3 w-64 h-64 rounded-full blur-2xl animate-pulse bg-[#34A853]" style={{ animationDelay: '3s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16 animate-fadeInUp">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
           Powerful <span className='text-[#4285F4]'>Features</span>
          </h2>
          <p className="text-lg font-medium text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            Everything you need to automate your content creation workflow
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <FeatureCard key={`${feature.title}-${index}`} {...feature} />
              ))}
        </div>
        

                  <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 mt-20">
              
                  <div className="relative mx-auto border-gray-800 dark:border-gray-700 bg-gradient-to-br from-gray-800 to-gray-900 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-2xl animate-fadeInUp" style={{ animationDelay: '200ms' }}>
                      <div className="w-[148px] h-[18px] bg-gray-800 top-0 rounded-b-[1rem] left-1/2 -translate-x-1/2 absolute"></div>
                      <div className="h-[46px] w-[3px] bg-gray-700 absolute -start-[17px] top-[124px] rounded-s-lg"></div>
                      <div className="h-[46px] w-[3px] bg-gray-700 absolute -start-[17px] top-[178px] rounded-s-lg"></div>
                      <div className="h-[64px] w-[3px] bg-gray-700 absolute -end-[17px] top-[142px] rounded-e-lg"></div>
                      <div className="rounded-[2rem] overflow-hidden w-[272px] h-[572px] bg-white dark:bg-gray-800 shadow-inner">
                          <img src={dark} className="dark:hidden w-[272px] h-[572px]" alt="Mobile Interface"/>
                          <img src={light} className="hidden dark:block w-[272px] h-[572px]" alt="Mobile Interface"/>
                      </div>
                  </div>

              <div className="flex flex-col items-center animate-fadeInUp" style={{ animationDelay: '400ms' }}>
                  {/* Laptop Mockup with glass effect */}
                  <div className="relative border-gray-800 dark:border-gray-700 bg-gradient-to-br from-gray-800 to-gray-900 border-[8px] rounded-t-xl w-full max-w-[320px] md:max-w-[600px] h-[200px] md:h-[380px] shadow-2xl">
                    <div className="rounded-lg overflow-hidden w-full h-full bg-white dark:bg-gray-800">
                      <img
                        src={pc1}
                        className="dark:hidden w-full h-full object-cover rounded-lg"
                        alt="Desktop Interface Light"
                      />
                      <img
                        src={pc2}
                        className="hidden dark:block w-full h-full object-cover rounded-lg"
                        alt="Desktop Interface Dark"
                      />
                    </div>
                  </div>

                  {/* Bottom Bar with subtle gradient */}
                  <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 dark:from-gray-700 dark:to-gray-800 rounded-b-xl rounded-t-sm h-[18px] md:h-[24px] w-full max-w-[380px] md:max-w-[700px] mt-3 shadow-lg">
                    <div className="absolute left-1/2 top-0 -translate-x-1/2 rounded-b-xl w-[64px] md:w-[110px] h-[5px] md:h-[9px] bg-gray-900"></div>
                  </div>
                </div>
            </div>

        {/* Supported Tools */}
        <div className="mt-20 animate-fadeInUp" style={{ animationDelay: '600ms' }}>
          <h3 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-12 text-center">
          <span>Supported </span>
            <span className="text-5xl md:text-6xl font-bold">
              <span className="text-[#4285F4]">G</span>
              <span className="text-[#EA4335]">o</span>
              <span className="text-[#FBBC04]">o</span>
              <span className="text-[#4285F4]">g</span>
              <span className="text-[#34A853]">l</span>
              <span className="text-[#EA4335]">e</span>
            </span>
            <span> Labs Tools</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
           {tools.map((tool, index) => (
              <ToolCard key={`${tool.title}-${index}`} {...tool} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
